import { AuthStatus } from '@amzn/bf-playback'
import { logWarn, noop, type Unsubscribe } from '@amazon/vinyl-util'
import { getAuth } from '@/auth/authRef'
import { awaitAuthSettled } from '@/auth/awaitAuthSettled'
import { loadActiveProfileId } from '@/auth/childProfile'
import { ctx } from '@/util/context'

const { target, errorHandler } = ctx('accountScope')

/** The active profile a token session (LWA, CBL, MAP) last resolved; read synchronously at boot. */
export const ACCOUNT_SCOPE_STORAGE_KEY = 'accountScope'

function readProfileScope(): string {
    try {
        return localStorage.getItem(ACCOUNT_SCOPE_STORAGE_KEY) ?? ''
    } catch {
        return ''
    }
}

function writeProfileScope(profileId: string | null): void {
    try {
        if (profileId) localStorage.setItem(ACCOUNT_SCOPE_STORAGE_KEY, profileId)
        else localStorage.removeItem(ACCOUNT_SCOPE_STORAGE_KEY)
    } catch {
        /* private-mode Safari: the scope stays unknown. */
    }
}

/**
 * `key` kept apart per account and profile: a cookie session's customer id, which a profile switch
 * changes, or a token session's active profile id.
 */
export function accountScopedKey(key: string): string {
    return `${key}:${accountScope()}`
}

/** The account and profile saved state is kept apart by; empty while unknown. */
export function accountScope(): string {
    const auth = getAuth()
    return auth.getCustomerId ? (auth.getCustomerId() ?? '') : readProfileScope()
}

/**
 * Resolves once {@link accountScopedKey} names the session's account and profile. A token session
 * waits only when signed in with no profile resolved yet; while PENDING (a MAP shell that has sent
 * no token) or signed out it resolves at once.
 */
export function awaitAccountScope(): Promise<void> {
    const auth = getAuth()
    if (auth.getCustomerId) return awaitAuthSettled()
    if (readProfileScope() || auth.status.value !== AuthStatus.SIGNED_IN) return Promise.resolve()
    return resolveProfileScope()
}

/**
 * Keeps a token session's scope on its active profile: resolved on every sign-in, forgotten on
 * every sign-out. A profile that differs from the one the boot read under reloads the page, so the
 * saved state already read is the new profile's.
 */
export function installAccountScope(): Unsubscribe {
    const auth = getAuth()
    if (auth.getCustomerId) return noop
    let previous: AuthStatus | undefined
    return auth.status.onData((status) => {
        // CBL boots SIGNED_OUT before its silent sign-in, so only a transition forgets the profile.
        if (status === AuthStatus.SIGNED_OUT && previous !== undefined) writeProfileScope(null)
        if (status === AuthStatus.SIGNED_IN && previous !== status) {
            resolveProfileScope().catch(errorHandler)
        }
        previous = status
    })
}

async function resolveProfileScope(): Promise<void> {
    const status$ = getAuth().status
    const under = status$.value
    const session = { ended: false }
    const unsubscribe = status$.onData((status) => {
        if (status !== under) session.ended = true
    })
    try {
        const profileId = await loadActiveProfileId()
        if (!session.ended && profileId) adoptProfileScope(profileId)
    } catch (err) {
        logWarn(target, 'active profile unavailable, saved state stays unscoped', err)
    } finally {
        unsubscribe()
    }
}

function adoptProfileScope(profileId: string): void {
    const previous = readProfileScope()
    if (previous === profileId) return
    writeProfileScope(profileId)
    if (previous) location.reload()
}
